"use client";

import { useSyncExternalStore } from "react";

type InputModality = "keyboard" | "pointer";

const GLOBAL_KEY = "__playmathInputModality";
type GlobalInputModalityState = {
  modality: InputModality;
  listeners: Set<() => void>;
  isInitialized: boolean;
  handlers?: {
    keydown: (e: KeyboardEvent) => void;
    pointerdown: (e: Event) => void;
    mousedown: (e: MouseEvent) => void;
    touchstart: (e: TouchEvent) => void;
  };
};

const globalState: GlobalInputModalityState = (
  globalThis as unknown as Record<string, GlobalInputModalityState | undefined>
)[GLOBAL_KEY] ?? {
  modality: "pointer",
  listeners: new Set<() => void>(),
  isInitialized: false,
};

// Persist singleton state across dev/HMR reloads.
(globalThis as unknown as Record<string, GlobalInputModalityState>)[GLOBAL_KEY] = globalState;

/**
 * This module intentionally installs singleton, process-lifetime listeners in the browser.
 * - In production this is desirable and avoids per-component listener churn.
 * - In development, the `globalThis`-backed `isInitialized` guard prevents duplicate installs
 *   in typical HMR setups. If your tooling fully re-evaluates modules with a fresh global
 *   (uncommon), you may need an explicit HMR dispose hook to remove listeners.
 */

function emit() {
  for (const listener of globalState.listeners) {
    listener();
  }
}

function setModality(next: InputModality) {
  if (globalState.modality === next) return;
  globalState.modality = next;
  emit();
}

function ensureInitialized() {
  if (globalState.isInitialized) return;
  if (typeof window === "undefined") return;
  globalState.isInitialized = true;

  if (!globalState.handlers) {
    globalState.handlers = {
      keydown: () => setModality("keyboard"),
      pointerdown: () => setModality("pointer"),
      mousedown: () => setModality("pointer"),
      touchstart: () => setModality("pointer"),
    };
  }

  window.addEventListener("keydown", globalState.handlers.keydown);
  // Pointer/touch interactions should not trigger focus previews
  window.addEventListener("pointerdown", globalState.handlers.pointerdown, { passive: true });
  window.addEventListener("mousedown", globalState.handlers.mousedown, { passive: true });
  window.addEventListener("touchstart", globalState.handlers.touchstart, { passive: true });

  const uninstall = () => {
    if (!globalState.handlers) return;
    window.removeEventListener("keydown", globalState.handlers.keydown);
    window.removeEventListener("pointerdown", globalState.handlers.pointerdown);
    window.removeEventListener("mousedown", globalState.handlers.mousedown);
    window.removeEventListener("touchstart", globalState.handlers.touchstart);
    globalState.isInitialized = false;
  };

  // Best-effort dev/HMR cleanup when supported by the tooling.
  if (process.env.NODE_ENV !== "production") {
    // webpack/Next-style HMR
    // biome-ignore lint/suspicious/noExplicitAny: dev-only optional hook
    const maybeModule: any = typeof module !== "undefined" ? module : undefined;
    if (maybeModule?.hot?.dispose) {
      maybeModule.hot.dispose(uninstall);
    }
  }
}

function subscribe(listener: () => void) {
  ensureInitialized();
  globalState.listeners.add(listener);
  return () => globalState.listeners.delete(listener);
}

function getSnapshotIsKeyboard() {
  return globalState.modality === "keyboard";
}

export function useIsKeyboardModality(): boolean {
  return useSyncExternalStore(subscribe, getSnapshotIsKeyboard, () => false);
}
